import { Component, EventEmitter, Inject, Input, OnChanges, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Store } from "@ngrx/store";

import {
	ReleaseDialogReturnData,
	ReleaseDialogType,
	TreatmentReleaseDialogComponent
} from "./treatment-release-dialog/treatment-release-dialog.component";
import { ORCADENT_PRODUCT_KEYS, ProductInfoForKey } from "libs/product/src/lib/models/product.model";
import { first } from "rxjs";
import { OrcadentTreatmentPackages, TreatmentModel, TreatmentPackages } from "libs/treatments/src/lib/models/treatment.model";
import { TreatmentState } from "libs/treatments/src/lib/store/treatment.reducer";
import { TreatmentRequested } from "libs/treatments/src/lib/store/treatment.actions";
import { OrderService } from "libs/order/src/lib/services/order.service";
import { ReleaseData } from "libs/anamnese/src/lib/models/anamnese.model";
import { AnamnesisService } from "libs/anamnese/src/lib/services/anamnesis.service";
import { MediaService } from "libs/media/src/lib/services/media.service";
import { MessagesRequested } from "libs/messages/src/lib/store/messages.actions";
import { QuestionnaireOrcadentDialogComponent } from "libs/questionaire/src/lib/orcadent/component/questionnaire-orcadent-dialog.component";
import { QuestionairePrintDialogComponent, QuestionairePrintDialogData } from "libs/questionaire/src/lib/questionaire-print/questionaire-print-dialog.component";

@Component({
	selector: 'tm2-treatment-release',
	templateUrl: './treatment-release.component.html',
	styleUrls: ['./treatment-release.component.scss']
})
export class TreatmentReleaseComponent implements OnChanges {
	@Input() treatment: TreatmentModel;
	@Output() backButtonClicked = new EventEmitter<any>();

	releaseData: ReleaseData = null;
	processing: boolean;

	constructor(
				private store: Store<TreatmentState>,
				public dialog: MatDialog,
				private orderService: OrderService,
				private mediaService: MediaService,
				private anamnesisService: AnamnesisService,
				@Inject('CONFIG') public config) {
	}


	ngOnChanges() {
		if (this.treatment.quotation?.anamnese?.releaseData) {
			this.releaseData = structuredClone(this.treatment.quotation.anamnese.releaseData);
		}

		if( this.isOrcadentTreatment() ){
			this.anamnesisService.getReleaseData(this.treatment._id)
			.pipe(first())
			.subscribe({
				next: (releaseData: ReleaseData) => {
					this.releaseData = releaseData;
				},
				error: (error) => {
					console.error("Error fetching treatment release data", error);
				}
			});
		}
	}

	onRelease() {
		this.processing = true;

		this.openCRDialog(ReleaseDialogType.RELEASE, this.treatment).afterClosed().subscribe((releaseDialogData: ReleaseDialogReturnData) => {
			if (releaseDialogData?.isApproved) {
				this.handleSetupRelease(ReleaseDialogType.RELEASE, releaseDialogData.comment);
			} else {
				this.processing = false;
			}
		});
	}

	onReleaseAlternativeOption() {
		this.processing = true;
		this.openCRDialog(ReleaseDialogType.RELEASE_ALTERNATIVE, this.treatment).afterClosed().subscribe((releaseDialogData: ReleaseDialogReturnData) => {
			if (releaseDialogData?.isApproved) {
				this.handleSetupRelease(ReleaseDialogType.RELEASE_ALTERNATIVE, releaseDialogData.comment);
			} else {
				this.processing = false;
			}
		});
	}


	onCorrection() {
		this.processing = true;

		this.openCRDialog(ReleaseDialogType.CORRECTION, this.treatment).afterClosed().subscribe((releaseDialogData: ReleaseDialogReturnData) => {
			if (releaseDialogData?.isApproved) {
				// setup correction
				this.orderService.requestSetupCorrection(
						this.treatment._id,
						releaseDialogData.comment
					)
					.subscribe({
						next: (response: TreatmentModel) => {
							this.store.dispatch(new MessagesRequested());
							this.store.dispatch(new TreatmentRequested({id: response._id}));
						},
						error: (error) =>
							console.error("Error while requesting setup correction", error)
					});
			} else {
				this.processing = false;
			}
		});
	}

	onCancel() {
		this.processing = true;

		this.openCRDialog(ReleaseDialogType.CANCEL, this.treatment).afterClosed().subscribe((releaseDialogData: ReleaseDialogReturnData) => {
			if (releaseDialogData?.isApproved) {
				this.orderService.cancelOrder(this.treatment._id, releaseDialogData.comment)
				.subscribe({
					next: (response: TreatmentModel) => {
						this.store.dispatch(new MessagesRequested());
						this.store.dispatch(new TreatmentRequested({id: response._id}));
					},
					error: (error) => console.error("Error while canceling order", error)
				}
			);
			} else {
				this.processing = false;
			}
		});
	}

	/**
	 * Handle the release action that places one of the following orders in the backend:
	 * - originally requested product order (chosenAction: RELEASE)
	 * - alternative option product order (chosenAction: RELEASE_ALTERNATIVE)
	 */
	handleSetupRelease(
		chosenAction: ReleaseDialogType,
		comment: string
	) {
		const isAlternativeOptionRelease = chosenAction === ReleaseDialogType.RELEASE_ALTERNATIVE;
		this.orderService.alignerReleaseOrder( this.treatment._id, comment, isAlternativeOptionRelease)
			.subscribe({
			next: (response: TreatmentModel) => {
				this.store.dispatch(new MessagesRequested());
				this.store.dispatch(new TreatmentRequested({id: response._id}));
			},
			error: (error) => console.error("Error while ordering setup", error)
		});
	}

	openCRDialog(type: ReleaseDialogType, treatment: TreatmentModel = null) {
		return this.dialog
			.open(TreatmentReleaseDialogComponent,
				{
					panelClass: 'blue-dialog',
					data: {type, treatment}
				}
			);
	}

	openQuestionaireDialog() {
		if (this.isOrcadentTreatment()) {
			this.anamnesisService.getOrcadentQuestionnaireData(this.treatment._id)
				.pipe(first())
				.subscribe({
					next: (questionnaireData) => {
						const initialWidth = '95%';
						const initialHeight = '95%';
						this.dialog.open(QuestionnaireOrcadentDialogComponent,
							{data: {
								questionnaireData: questionnaireData,
								width: initialWidth,
								height: initialHeight
							},
							width: initialWidth, height: initialHeight});
					},
					error: (error) => {
						console.error("Error fetching treatment release data", error);
					}
			});
			return;
		}

		const dialogData: QuestionairePrintDialogData = {
			treatment: this.treatment,
		};
		this.dialog.open(QuestionairePrintDialogComponent,
			{data: dialogData, maxWidth: '100vw', width: '100%', height: '100%'});

	}

	getAlternativeOptionProductName() : string
	{
		const productKey = this.treatment.upgrade.productKey;
		return ProductInfoForKey[productKey]?.name;
	}

	downloadMediaFile(id) {
		this.mediaService.getFile(id);
	}

	hasRequestedOptionForRelease(): boolean {
		return (this.treatment?.packages as TreatmentPackages)?.steps?.length > 0
			|| (this.treatment?.packages as OrcadentTreatmentPackages)?.pending?.length > 0 ;
	}

	hasAlternativeOptionForRelease(): boolean {
		return !!this.treatment.upgrade;
	}

	isOrcadentTreatment(): boolean {
		return ORCADENT_PRODUCT_KEYS.includes(this.treatment.product.key);
	}

	onBackButtonClicked() {
		this.backButtonClicked.emit();
	}
}
